<template>
    <Head title="Incident Statistics & Analytics Report" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <div>
                <Link :href="route('reports.incidents.index')" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2 inline-flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Incident Statistics & Analytics</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    View comprehensive incident statistics, trends, and safety performance metrics
                </p>
            </div>
        </div>

        <!-- Key Metrics Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <!-- Total Incidents -->
            <Card class="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200 dark:border-red-800">
                <div class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-red-700 dark:text-red-400">Total Incidents</p>
                            <p class="text-2xl font-bold text-red-900 dark:text-red-200 mt-1">{{ metrics.totalIncidents }}</p>
                        </div>
                        <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div class="mt-2 flex items-center text-xs">
                        <span :class="metrics.incidentTrend >= 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'">
                            {{ metrics.incidentTrend >= 0 ? '+' : '' }}{{ metrics.incidentTrend }}%
                        </span>
                        <span class="ml-1 text-red-600 dark:text-red-400">vs previous period</span>
                    </div>
                </div>
            </Card>

            <!-- MTTA -->
            <Card class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
                <div class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-blue-700 dark:text-blue-400">MTTA</p>
                            <p class="text-2xl font-bold text-blue-900 dark:text-blue-200 mt-1">{{ metrics.mtta }}h</p>
                        </div>
                        <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p class="mt-2 text-xs text-blue-600 dark:text-blue-400">Mean Time to Acknowledge</p>
                </div>
            </Card>

            <!-- MTTR -->
            <Card class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800">
                <div class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-purple-700 dark:text-purple-400">MTTR</p>
                            <p class="text-2xl font-bold text-purple-900 dark:text-purple-200 mt-1">{{ metrics.mttr }}h</p>
                        </div>
                        <svg class="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p class="mt-2 text-xs text-purple-600 dark:text-purple-400">Mean Time to Resolve</p>
                </div>
            </Card>

            <!-- SLA Compliance -->
            <Card class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
                <div class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-green-700 dark:text-green-400">SLA Compliance</p>
                            <p class="text-2xl font-bold text-green-900 dark:text-green-200 mt-1">{{ metrics.slaCompliance }}%</p>
                        </div>
                        <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                    </div>
                    <p class="mt-2 text-xs text-green-600 dark:text-green-400">On-time resolution rate</p>
                </div>
            </Card>

            <!-- Investigation Count -->
            <Card class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-800">
                <div class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-amber-700 dark:text-amber-400">Investigations</p>
                            <p class="text-2xl font-bold text-amber-900 dark:text-amber-200 mt-1">{{ investigationMetrics.total }}</p>
                        </div>
                        <svg class="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <p class="mt-2 text-xs text-amber-600 dark:text-amber-400">{{ investigationMetrics.completed }} completed</p>
                </div>
            </Card>
        </div>

        <!-- Charts -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Severity Distribution -->
            <Card>
                <div class="p-6">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Severity Distribution</h3>
                    <PieChart
                        v-if="severityData.length > 0"
                        :data="prepareSeverityChartData()"
                        :height="300"
                    />
                    <p v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No data available</p>
                </div>
            </Card>

            <!-- Status Distribution -->
            <Card>
                <div class="p-6">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Status Distribution</h3>
                    <BarChart
                        v-if="statusData.length > 0"
                        :data="prepareStatusChartData()"
                        :height="300"
                    />
                    <p v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No data available</p>
                </div>
            </Card>
        </div>

        <!-- Type Distribution -->
        <Card>
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Incident Type Distribution</h3>
                <BarChart
                    v-if="typeDistribution.length > 0"
                    :data="prepareTypeChartData()"
                    :height="300"
                />
                <p v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No data available</p>
            </div>
        </Card>

        <!-- Investigation Metrics -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card class="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-800">
                <div class="p-6">
                    <h3 class="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-4">Investigation Metrics</h3>
                    <div class="space-y-3">
                        <div class="flex justify-between items-center">
                            <span class="text-xs text-indigo-700 dark:text-indigo-400">Total Investigations</span>
                            <span class="text-sm font-semibold text-indigo-900 dark:text-indigo-200">{{ investigationMetrics.total }}</span>
                        </div>
                        <div class="flex justify-between items-center">
                            <span class="text-xs text-blue-700 dark:text-blue-400">In Progress</span>
                            <span class="text-sm font-semibold text-blue-900 dark:text-blue-200">{{ investigationMetrics.inProgress }}</span>
                        </div>
                        <div class="flex justify-between items-center">
                            <span class="text-xs text-green-700 dark:text-green-400">Completed</span>
                            <span class="text-sm font-semibold text-green-900 dark:text-green-200">{{ investigationMetrics.completed }}</span>
                        </div>
                        <div class="flex justify-between items-center pt-2 border-t border-indigo-200 dark:border-indigo-700">
                            <span class="text-xs text-indigo-700 dark:text-indigo-400">Avg Duration</span>
                            <span class="text-sm font-semibold text-indigo-900 dark:text-indigo-200">{{ investigationMetrics.avgDuration }} days</span>
                        </div>
                    </div>
                </div>
            </Card>

            <!-- Severity Breakdown -->
            <Card class="bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20 border-rose-200 dark:border-rose-800">
                <div class="p-6">
                    <h3 class="text-sm font-medium text-rose-900 dark:text-rose-300 mb-4">Severity Breakdown</h3>
                    <div class="space-y-3">
                        <div v-for="severity in severityData" :key="severity.name" class="flex justify-between items-center">
                            <span class="text-xs text-rose-700 dark:text-rose-400">{{ severity.name }}</span>
                            <span class="text-sm font-semibold text-rose-900 dark:text-rose-200">{{ severity.count }} ({{ severity.percentage }}%)</span>
                        </div>
                    </div>
                </div>
            </Card>

            <!-- Status Breakdown -->
            <Card class="bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 border-teal-200 dark:border-teal-800">
                <div class="p-6">
                    <h3 class="text-sm font-medium text-teal-900 dark:text-teal-300 mb-4">Status Breakdown</h3>
                    <div class="space-y-3">
                        <div v-for="status in statusData" :key="status.name" class="flex justify-between items-center">
                            <span class="text-xs text-teal-700 dark:text-teal-400">{{ status.name }}</span>
                            <span class="text-sm font-semibold text-teal-900 dark:text-teal-200">{{ status.count }} ({{ status.percentage }}%)</span>
                        </div>
                    </div>
                </div>
            </Card>
        </div>

        <!-- Key Insights -->
        <Card class="bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-800">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-amber-900 dark:text-amber-300 mb-2">Key Insights</h3>
                <p class="text-sm text-amber-800 dark:text-amber-400">
                    {{ metrics.totalIncidents }} incidents reported in the selected period.
                    Average time to acknowledge: {{ metrics.mtta }} hours.
                    Average time to resolve: {{ metrics.mttr }} hours.
                    SLA compliance rate: {{ metrics.slaCompliance }}%.
                    {{ investigationMetrics.total }} investigations initiated with {{ investigationMetrics.completed }} completed.
                </p>
            </div>
        </Card>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import BarChart from '@/Components/Charts/BarChart.vue';
import PieChart from '@/Components/Charts/PieChart.vue';
import route from '@/Utils/route';

const props = defineProps({
    metrics: Object,
    severityData: Array,
    statusData: Array,
    typeDistribution: Array,
    investigationMetrics: Object,
});

const prepareSeverityChartData = () => {
    return {
        labels: props.severityData.map(s => s.name),
        datasets: [{
            data: props.severityData.map(s => s.count),
            backgroundColor: [
                'rgba(156, 163, 175, 0.8)',  // gray - low
                'rgba(234, 179, 8, 0.8)',    // yellow - medium
                'rgba(249, 115, 22, 0.8)',   // orange - high
                'rgba(239, 68, 68, 0.8)',    // red - critical
            ],
        }],
    };
};

const prepareStatusChartData = () => {
    return {
        labels: props.statusData.map(s => s.name),
        datasets: [{
            label: 'Incidents',
            data: props.statusData.map(s => s.count),
            backgroundColor: [
                'rgba(59, 130, 246, 0.8)',   // blue - open
                'rgba(99, 102, 241, 0.8)',   // indigo - triaged
                'rgba(147, 51, 234, 0.8)',   // purple - in_progress
                'rgba(34, 197, 94, 0.8)',    // green - completed
                'rgba(107, 114, 128, 0.8)',  // gray - closed
            ],
        }],
    };
};

const prepareTypeChartData = () => {
    return {
        labels: props.typeDistribution.map(t => t.name),
        datasets: [{
            label: 'Incidents',
            data: props.typeDistribution.map(t => t.count),
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
        }],
    };
};
</script>
